import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import {
  ListObject,
  ListServerPaginationStateModel,
  LIST_SERVER_PAGINATION_INITIAL_STATE,
} from '../../../../../common/models';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { MasterDataType } from '../constants';
import { MasterDataReportItemModel } from '../models';
import {
  MasterDataReportItemInitialState,
  MasterDataReportItemListLoad,
  MasterDataReportItemSetInitialData,
} from './master-data-report-item.actions';
import { MasterDataReportItemService } from './master-data-report-item.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface MasterDataReportItemStateModel {
  list: ListServerPaginationStateModel<MasterDataReportItemModel>;
  id: string;
  type: MasterDataType;
}

const initialState: MasterDataReportItemStateModel = {
  list: LIST_SERVER_PAGINATION_INITIAL_STATE,
  id: null,
  type: null,
};

@State<MasterDataReportItemStateModel>({
  name: 'masterDataReportItem',
  defaults: initialState,
})
@Injectable()
export class MasterDataReportItemState {
  constructor(
    public masterDataReportItemService: MasterDataReportItemService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: MasterDataReportItemStateModel): MasterDataReportItemStateModel {
    return state;
  }

  @Selector()
  static getList(state: MasterDataReportItemStateModel): ListServerPaginationStateModel<MasterDataReportItemModel> {
    return state.list;
  }

  @Selector()
  static getId(state: MasterDataReportItemStateModel): string {
    return state.id;
  }

  @Selector()
  static getType(state: MasterDataReportItemStateModel): MasterDataType {
    return state.type;
  }

  @Selector()
  static getLoaded(state: MasterDataReportItemStateModel): boolean {
    return state.list.loaded;
  }

  @Action(MasterDataReportItemListLoad)
  getReportItemList(
    ctx: StateContext<MasterDataReportItemStateModel>,
    { payload }: MasterDataReportItemListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const masterDataId = MasterDataReportItemState.getId(ctx.getState());
    const type = MasterDataReportItemState.getType(ctx.getState());

    return this.masterDataReportItemService.getReportItemList(masterDataId, type, payload).pipe(
      take(1),
      map((value: ListObject<MasterDataReportItemModel>) => {
        ctx.patchState({ list: { ...value, loaded: true } });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(MasterDataReportItemInitialState)
  setInitialState(ctx: StateContext<MasterDataReportItemStateModel>): void {
    ctx.setState(initialState);
  }

  @Action(MasterDataReportItemSetInitialData)
  setReportItemInitData(
    ctx: StateContext<MasterDataReportItemStateModel>,
    { payload }: MasterDataReportItemSetInitialData,
  ): void {
    ctx.patchState({ id: payload.id, type: payload.type });
  }
}
